import { ObjectField, SchemaType } from "sanity";

export type OptionValue = string | boolean | null;

export type ComponentOption = {
  key: string;
  title: string;
  group?: string;
  field: string;
  values: { title: string; value: OptionValue }[];
};

export type SectionFilter = Pick<ComponentOption, "group" | "field"> & {
  value: OptionValue;
};

const NOT_SET = { title: "(not set)", value: null };

const listedValues = (type: SchemaType): ComponentOption["values"] => {
  if (type.jsonType === "boolean") {
    return [
      { title: "Yes", value: true },
      { title: "No", value: false },
      NOT_SET
    ];
  }
  if (type.jsonType !== "string" || !type.options?.list) return [];

  return [
    ...type.options.list.map((item) =>
      typeof item === "string"
        ? { title: item, value: item }
        : { title: item.title ?? String(item.value), value: item.value ?? null }
    ),
    NOT_SET
  ];
};

const fieldOption = (field: ObjectField, group?: ObjectField) => {
  const values = listedValues(field.type);
  const title = field.type.title ?? field.name;

  return values.length === 0
    ? []
    : [
        {
          key: group ? `${group.name}.${field.name}` : field.name,
          title: group ? `${group.type.title ?? group.name}: ${title}` : title,
          ...(group && { group: group.name }),
          field: field.name,
          values
        }
      ];
};

// Filterable options: enum and boolean fields, one object level deep.
export const getComponentOptions = (type?: SchemaType): ComponentOption[] => {
  if (type?.jsonType !== "object") return [];

  return type.fields.flatMap((field) =>
    field.type.jsonType === "object"
      ? field.type.fields.flatMap((nested) => fieldOption(nested, field))
      : fieldOption(field)
  );
};

export const sectionCondition = (filter?: SectionFilter) => {
  if (!filter) return "_type == $componentKey";

  const target = filter.group ? "@[$group][$field]" : "@[$field]";
  const match =
    filter.value === null ? `!defined(${target})` : `${target} == $value`;

  return `_type == $componentKey && ${match}`;
};
